import type { ReactNode } from "react";
import { ChevronRight, type LucideIcon } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { TabBar } from "@/components/ui/tab-bar";
import { MODAL_SECCIONES, MODAL_SECCIONES_ALTO } from "@/components/ui/panel-widths";
import { cn } from "@/lib/utils";

export interface SeccionModal<T extends string = string> {
  id: T;
  label: string;
  icon: LucideIcon;
  /** Texto corto bajo el título de la sección, en el encabezado del contenido. */
  descripcion?: string;
}

/**
 * Modal con menú de secciones a la izquierda (Mi cuenta, configuraciones): ancho y alto fijos, así
 * que al cambiar de sección la ventana no cambia de tamaño. Desde `sm` es de dos columnas: arriba a
 * la izquierda la identidad (`encabezado`), el menú vertical y abajo `pie`; a la derecha el título
 * de la sección y su contenido con scroll propio. En móvil sale como hoja inferior con el
 * encabezado arriba y el menú como `TabBar` con scroll lateral.
 */
export function ModalSecciones<T extends string>({
  open,
  onOpenChange,
  titulo,
  descripcion,
  encabezado,
  pie,
  secciones,
  value,
  onChange,
  accion,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Título accesible de la ventana (lectores de pantalla); no se pinta. */
  titulo: string;
  descripcion: string;
  encabezado: ReactNode;
  pie?: ReactNode;
  secciones: SeccionModal<T>[];
  value: T;
  onChange: (id: T) => void;
  /** Acción principal de la sección abierta (Editar, Guardar), a la derecha de su título. */
  accion?: ReactNode;
  children: ReactNode;
}) {
  const actual = secciones.find((s) => s.id === value) ?? secciones[0];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn("flex flex-col gap-0 overflow-hidden p-0 sm:flex-row", MODAL_SECCIONES, MODAL_SECCIONES_ALTO)}
      >
        <DialogTitle className="sr-only">{titulo}</DialogTitle>
        <DialogDescription className="sr-only">{descripcion}</DialogDescription>

        <aside className="flex shrink-0 flex-col border-b border-border bg-muted/30 sm:w-64 sm:border-b-0 sm:border-r">
          <div className="px-4 pb-3 pt-5 pr-12 sm:px-5 sm:pb-5 sm:pt-7 sm:pr-5">{encabezado}</div>

          <nav aria-label="Secciones" className="hidden min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto px-3 py-2 sm:flex">
            {secciones.map((s) => {
              const activa = s.id === actual?.id;
              const Icon = s.icon;
              return (
                <button
                  key={s.id}
                  type="button"
                  aria-current={activa ? "page" : undefined}
                  onClick={() => onChange(s.id)}
                  className={cn(
                    "group flex min-h-[40px] w-full items-center gap-2.5 rounded-md px-3 text-left text-[13px] font-medium transition-colors",
                    "outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/30",
                    activa
                      ? "bg-background text-primary shadow-sm ring-1 ring-border"
                      : "text-muted-foreground hover:bg-background/60 hover:text-foreground",
                  )}
                >
                  <Icon className="size-4 shrink-0" strokeWidth={1.75} aria-hidden />
                  <span className="min-w-0 flex-1 truncate">{s.label}</span>
                  <ChevronRight
                    className={cn("size-3.5 shrink-0 transition-opacity", activa ? "opacity-100" : "opacity-0 group-hover:opacity-60")}
                    aria-hidden
                  />
                </button>
              );
            })}
          </nav>

          <TabBar<T>
            size="md"
            className="px-2 sm:hidden"
            tabs={secciones}
            value={actual?.id ?? value}
            onChange={(id) => onChange(id)}
          />

          {pie && <div className="hidden px-5 py-3 sm:block">{pie}</div>}
        </aside>

        <section className="flex min-h-0 min-w-0 flex-1 flex-col">
          {/* Sin línea divisoria: el título y el aire bastan. La acción principal de la sección (CTA)
              va aquí, a la derecha del título, no dentro del contenido. */}
          <header className="flex shrink-0 items-start justify-between gap-3 px-4 pb-2 pt-4 sm:px-6 sm:pb-3 sm:pr-14 sm:pt-6">
            <div className="min-w-0">
              <h2 className="text-base font-bold leading-snug text-foreground sm:text-lg">{actual?.label}</h2>
              {actual?.descripcion && <p className="mt-0.5 text-[12.5px] text-muted-foreground">{actual.descripcion}</p>}
            </div>
            {accion && <div className="flex shrink-0 items-center gap-2">{accion}</div>}
          </header>
          <div className="min-h-0 flex-1 space-y-8 overflow-y-auto px-4 pb-6 pt-3 sm:px-6">{children}</div>
        </section>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Fila de ajuste dentro de una sección: nombre y explicación a la izquierda, control a la derecha
 * (switch, botón). Van seguidas dentro de `ListaAjustes`, separadas por aire, sin líneas ni tarjeta.
 */
export function FilaAjuste({
  titulo,
  descripcion,
  htmlFor,
  children,
}: {
  titulo: ReactNode;
  descripcion?: ReactNode;
  /** id del control, para que el nombre funcione como su etiqueta. */
  htmlFor?: string;
  children: ReactNode;
}) {
  const Titulo = htmlFor ? "label" : "p";
  return (
    <div className="flex flex-col gap-3 py-3.5 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
      <div className="min-w-0">
        <Titulo htmlFor={htmlFor} className="block text-sm font-medium text-foreground">{titulo}</Titulo>
        {descripcion && <p className="mt-0.5 text-[12.5px] leading-snug text-muted-foreground">{descripcion}</p>}
      </div>
      <div className="flex shrink-0 items-center gap-2">{children}</div>
    </div>
  );
}

export function ListaAjustes({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn("flex flex-col", className)}>{children}</div>;
}

/** Fila de formulario: etiqueta (y ayuda) a la izquierda desde `sm`, campo a la derecha. */
export function FilaCampo({
  id,
  label,
  ayuda,
  children,
}: {
  id: string;
  label: ReactNode;
  ayuda?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="grid gap-1.5 py-3 first:pt-0 sm:grid-cols-[170px_minmax(0,1fr)] sm:items-start sm:gap-4">
      <div className="sm:pt-2">
        <label htmlFor={id} className="block text-sm font-medium text-foreground">{label}</label>
        {ayuda && <p className="mt-0.5 text-[11.5px] leading-snug text-muted-foreground">{ayuda}</p>}
      </div>
      <div className="min-w-0">{children}</div>
    </div>
  );
}
